import { Component, type ErrorInfo, type ReactNode } from "react";
import { trackError } from "@/utils/error-monitoring";
import { getLoqateErrorDetails, isNetworkError } from "./loqate-error";

export type AddressSearchFailure = "loqate-api" | "network" | "render";

type AddressAutocompleteErrorBoundaryProps = {
  onError: (failure: AddressSearchFailure) => void;
  children: ReactNode;
};

type AddressAutocompleteErrorBoundaryState = {
  hasError: boolean;
};

function reportFailure(
  error: Error,
  errorInfo: ErrorInfo,
): AddressSearchFailure {
  const loqateDetails = getLoqateErrorDetails(error);

  if (loqateDetails) {
    trackError(error, {
      payload: {
        component: "AddressAutocomplete",
        source: "loqate-api",
        ...loqateDetails,
      },
    });
    return "loqate-api";
  }

  if (isNetworkError(error)) {
    // A lost request says nothing about our code, and the donor is already
    // on the manual form: it is a warning, and the component stack would
    // only bury the one fact that matters.
    trackError(error, {
      level: "warning",
      payload: {
        component: "AddressAutocomplete",
        source: "network",
      },
    });
    return "network";
  }

  trackError(error, {
    payload: {
      component: "AddressAutocomplete",
      source: "render",
      componentStack: errorInfo.componentStack,
    },
  });
  return "render";
}

/**
 * Catches whatever the address search throws and hands the donor the manual
 * form instead.
 *
 * We report the error ourselves rather than using `@sentry/react`'s
 * `ErrorBoundary` because most of what lands here is not a render crash, and
 * `beforeCapture` can only add tags. react-loqate re-throws during render both
 * the errors Loqate answers with — a code, a cause and a resolution — and the
 * ones `fetch` rejects with when the request never arrives. Telling the three
 * apart is the difference between "Text or Container Required", "the donor
 * lost signal" and a bug worth chasing.
 */
export class AddressAutocompleteErrorBoundary extends Component<
  AddressAutocompleteErrorBoundaryProps,
  AddressAutocompleteErrorBoundaryState
> {
  state: AddressAutocompleteErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AddressAutocompleteErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.onError(reportFailure(error, errorInfo));
  }

  render() {
    if (this.state.hasError) {
      return null;
    }

    return this.props.children;
  }
}
